/**
 * ParaParaView Main App - 正規化グラフ構造版
 */

import { useEffect, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useStore, useError } from './store'
import { AppRouter } from './router/AppRouter'
import { LoadingScreen } from './components/layout/LoadingScreen'
import { ErrorScreen } from './components/layout/ErrorScreen'
import { ErrorBoundary } from './components/ErrorBoundary'
import { QueryErrorBoundary } from './components/QueryErrorBoundary'
import { queryClient } from './lib/queryClient'
import { QueryDevPanel } from './components/QueryDevPanel'
import { APP_VERSION, COMMIT_HASH } from './version'
import { logger } from './lib/logger'

function App() {
  const [isInitializing, setIsInitializing] = useState(true)
  const error = useError()
  const initialize = useStore(state => state.initialize)
  const setError = useStore(state => state.setError)

  // ビューポート高さの動的設定（モバイルブラウザUI対応）
  useEffect(() => {
    const setAppHeight = () => {
      // 実際のビューポート高さを取得
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
      
      // デバッグログ
      logger.log('[App] Viewport height updated:', window.innerHeight, 'vh:', vh)
    }

    // 初期設定
    setAppHeight()

    // イベントリスナー登録
    window.addEventListener('resize', setAppHeight)
    window.addEventListener('orientationchange', setAppHeight)
    
    // iOS Safari対策: スクロール時にも更新
    let timeoutId: number
    const handleScroll = () => {
      clearTimeout(timeoutId)
      timeoutId = window.setTimeout(setAppHeight, 100)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('resize', setAppHeight)
      window.removeEventListener('orientationchange', setAppHeight)
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(timeoutId)
    }
  }, [])

  // アプリ初期化
  useEffect(() => {
    logger.log(`ParaParaView v${APP_VERSION} (${COMMIT_HASH}) initializing...`)
    
    const initApp = async () => {
      await initialize()
      setIsInitializing(false)
    }
    void initApp()
  }, [initialize])

  // エラー表示
  useEffect(() => {
    if (error) {
      logger.error('[App Error]:', error)
      // 3秒後にエラーをクリア
      const timer = setTimeout(() => setError(null), 3000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [error, setError])

  // 初期化中のローディング表示
  if (isInitializing) {
    return <LoadingScreen />
  }

  // エラー表示
  if (error) {
    return <ErrorScreen error={error} />
  }

  // メインコンテンツ - QueryClientProviderとErrorBoundaryでラップ
  return (
    <QueryClientProvider client={queryClient}>
      <QueryErrorBoundary>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </QueryErrorBoundary>
      {/* 開発環境でのみReact Query DevToolsを表示 */}
      {process.env.NODE_ENV === 'development' && (
        <>
          <ReactQueryDevtools initialIsOpen={false} />
          <QueryDevPanel />
        </>
      )}
    </QueryClientProvider>
  )
}

export default App